import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';

// Portal access for one client contact: which sections they reach, the invite
// that lets them in, and the controls for when they lock themselves out.
//
// Before this screen, onboarding a client meant two hand-written SQL updates.
//
// The pills are the whole point: a section is a row rather than a column, so a
// client capability added later appears here on its own without a migration or
// a new screen.

const fmtWhen = (iso) => {
  if (!iso) return null;
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function PortalAccessPanel({ contact, onClose }) {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    api.get(`client-portal-access/${contact.id}`)
      .then(r => setAccess(r.access))
      .catch(e => toast.error(e.data?.error || 'Could not load portal access.'));
  }, [contact.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.access) setAccess(r.access);
      if (okMessage) toast.success(okMessage);
      return r;
    } catch (e) {
      toast.error(e.data?.error || 'That did not work.');
      return null;
    } finally { setBusy(false); }
  };

  if (!access) {
    return <div className="text-[9px] text-gray-400 py-2">Loading portal access…</div>;
  }

  const held = access.features.filter(f => f.held);
  const neverSignedIn = access.is_portal_active && !access.last_login;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-2">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[11px] font-bold text-navy">
            Portal access — {contact.first_name} {contact.last_name}
          </div>
          <div className="text-[8px] text-gray-400">
            {contact.email || <span className="text-amber-700 font-bold">no email address, so they cannot be invited</span>}
          </div>
        </div>
        <button onClick={onClose} className="text-[8px] text-gray-400 hover:text-gray-600">Close</button>
      </div>

      {/* Sections. One pill per known section, so a capability added later
          shows up here on its own. */}
      <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Sections they can reach</div>
      <div className="flex flex-wrap gap-1.5 mb-1">
        {access.features.map(f => (
          <button key={f.key} disabled={busy || !f.togglable}
            title={
              f.derived ? 'Comes from a contract naming them as approver, so it cannot be switched off here.'
              : !f.client_enabled ? 'Switch this on for the client first, at the top of the client row.'
              : f.blurb
            }
            onClick={() => run(
              () => api.put(`client-portal-access/${contact.id}/features`, { feature_key: f.key, granted: !f.held }),
              f.held ? `${f.label} switched off` : `${f.label} switched on`,
            )}
            className={`border rounded-full px-3 py-1 text-[9px] font-bold ${
              f.held ? 'bg-navy border-navy text-white'
              : f.granted_to_contact && !f.client_enabled ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            } ${!f.togglable ? 'opacity-70 cursor-not-allowed' : ''}`}>
            {f.label}
            {f.derived ? ' · from contract' : ''}
            {!f.derived && f.granted_to_contact && !f.client_enabled ? ' · off for the client' : ''}
          </button>
        ))}
      </div>
      <div className="text-[8px] text-gray-400 mb-4">
        {held.length === 0
          ? 'Nothing switched on: they would sign in and reach nothing, so an invite is refused.'
          : held.length === 1
            ? `They land straight in ${held[0].label.toLowerCase()}.`
            : 'They choose from these when they sign in.'}
      </div>

      {/* Account state and the controls for it. */}
      <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Account</div>
      <div className="flex flex-wrap items-center gap-2 text-[9px]">
        <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold border ${
          access.is_portal_active
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
          {access.is_portal_active ? 'Active' : 'Not active'}
        </span>
        {access.locked_at && (
          <span className="rounded-full px-2 py-0.5 text-[8px] font-bold border bg-red-50 border-red-200 text-red-700">
            Locked out
          </span>
        )}
        <span className="text-gray-500">
          {access.last_login
            ? `Last sign-in ${fmtWhen(access.last_login)}`
            : neverSignedIn ? 'Invited, not yet signed in' : 'Never signed in'}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-3">
        <button disabled={busy || !contact.email || held.length === 0}
          onClick={async () => {
            const r = await run(
              () => api.post(`client-portal-access/${contact.id}/invite`, {}),
              'Invite sent',
            );
            if (r?.temp_password) setInvite(r);
            load();
          }}
          className="btn-primary rounded px-3 py-1 text-[8px] font-bold disabled:opacity-40">
          {access.is_portal_active ? 'Reset password & resend' : 'Send invite'}
        </button>

        <button disabled={busy}
          onClick={() => run(
            () => api.put(`client-portal-access/${contact.id}/active`, { is_portal_active: !access.is_portal_active }),
            access.is_portal_active ? 'Portal access switched off' : 'Portal access switched on',
          )}
          className="btn-secondary border rounded px-3 py-1 text-[8px] font-bold">
          {access.is_portal_active ? 'Switch off' : 'Switch on'}
        </button>

        {access.locked_at && (
          <button disabled={busy}
            onClick={() => run(() => api.post(`client-portal-access/${contact.id}/unlock`, {}), 'Account unlocked')}
            className="border border-amber-300 bg-amber-50 text-amber-800 rounded px-3 py-1 text-[8px] font-bold">
            Unlock
          </button>
        )}
      </div>

      {/* Shown once, to the person who pressed the button, so they can read it
          out if the email is slow. It is not stored anywhere readable. */}
      {invite && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-[9px] text-amber-800">
          <div className="font-bold mb-0.5">Invite sent to {contact.email}</div>
          <div>
            Temporary password: <span className="font-mono font-bold">{invite.temp_password}</span>
          </div>
          <div className="text-[8px] mt-0.5">
            They will be asked to choose their own on first sign-in. This is shown once.
          </div>
        </div>
      )}
    </div>
  );
}
